import { UserSortOption } from '@rematter/frontend-lib-api-client';

import { UserRecord, UserStatus } from '../types';

import { createQueryHook } from '~/shared/providers/api';

export interface UsersQueryParams {
  searchTerm?: string;
  facilityIds?: string[];
  roleIds?: string[];
  sorting?: UserSortOption[];
  limit?: number;
  offset?: number;
}

export interface UsersQueryResult {
  users: UserRecord[];
  total: number;
}

/**
 * Query hook for fetching users for the Team & Permissions module.
 * Transforms user data into UserRecord format.
 *
 * Note: The roles array is left empty here. Use useUserRolesMapQuery
 * to enrich users with real role names via coreUserRoleAssignments.
 */
export const useUsersQuery = createQueryHook(
  'settings.teamPermissions.users',
  (params: UsersQueryParams) => ({
    coreUsersByOrganization: {
      __args: {
        filter: params.searchTerm
          ? {
              searchTerm: params.searchTerm,
            }
          : undefined,
        sorting: params.sorting,
        pagination: {
          limit: params.limit ?? 20,
          offset: params.offset ?? 0,
        },
      },
      entries: {
        id: true,
        firstName: true,
        lastName: true,
        email: true,
        phoneNumber: true,
        status: true,
        facilities: {
          id: true,
          facilityName: true,
        },
      },
      page: {
        offset: true,
        limit: true,
        total: true,
      },
    },
  }),
  {
    select: (data): UsersQueryResult => ({
      users:
        data?.coreUsersByOrganization?.entries?.map((user) => ({
          id: user.id,
          name: `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || '-',
          roles: [],
          roleIds: [],
          email: user.email ?? '',
          phoneNumber: user.phoneNumber ?? null,
          facilities:
            user.facilities?.map((f) => f.facilityName).filter(Boolean) ?? [],
          facilityIds: user.facilities?.map((f) => f.id).filter(Boolean) ?? [],
          status: (user.status === 'ARCHIVED'
            ? 'archived'
            : 'active') as UserStatus,
        })) ?? [],
      total:
        data?.coreUsersByOrganization?.page?.total ??
        data?.coreUsersByOrganization?.entries?.length ??
        0,
    }),
  },
);
